import { Button, Checkbox, Classes, Dialog, Intent, Label, NumericInput, Radio, RadioGroup } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { TranslatedMessage } from './translation';
import { TLCode } from '../../common/enums';
import { Form } from 'react-final-form';
import {
  PharmacyFormField,
  TextInputFormField,
  RTEFormField,
  GroupFormField,
  PharmacyFormField2,
} from '../common/form';
import Select from 'react-select';
import { WithTranslateProps } from './WithTranslate';
import { DivLine } from './Toolbox';
import DateInput from '../common/input/DateInput';
import { Instance } from 'mobx-state-tree';
import { Pharmacy } from '../../store/models';
import { Group } from '../../store/models/Group';
import { MessageToSend } from '../../store/models/Message';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import styled from '@emotion/styled';

interface MessageDialogProps extends WithTranslateProps {
  isOpen: boolean;
  onClose: () => void;
  pharmacyId: number | undefined;
  secretariatId: number | undefined;
  save: (messageToSave: any) => void;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  groupList: Array<Instance<typeof Group>>;
  isSecretariat: boolean;
  isTest?: boolean;
}

export const MessageDialog = ({
  isOpen,
  onClose,
  save,
  pharmacyId,
  secretariatId,
  pharmacyList,
  groupList,
  translate,
  isSecretariat,
  isTest = false,
}: MessageDialogProps) => {
  const defaultMessage = {
    messageId: 0,
    body: '',
    title: isTest ? 'Lorem ipsum dolor' : null,
    recipientId: isSecretariat ? undefined : secretariatId,
    recipientIds: [],
    selectionMode: isTest ? 'test' : (isSecretariat ? undefined : secretariatId) ? 'one' : '',
    dutyType: '',
    dutyDate: null,
    dayOnly: false,
    nightOnly: false,
    dayNight: false,
    radius: 0,
  } as MessageToSend;

  const [messageToSend, setMessageToSend] = useState(defaultMessage);

  const setRecipientIds = useCallback(
    (value: any) => {
      setMessageToSend({ ...messageToSend, recipientIds: value.map((v) => v.value) });
    },
    [setMessageToSend, messageToSend],
  );

  const setSelectionMode = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, selectionMode: data });
    },
    [setMessageToSend, messageToSend],
  );

  const onDutyDateChange = useCallback(
    (data: Date) => {
      setMessageToSend({ ...messageToSend, dutyDate: data });
    },
    [setMessageToSend, messageToSend],
  );

  const onNightOnlyChange = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, nightOnly: data.currentTarget.checked });
    },
    [setMessageToSend, messageToSend],
  );

  const onDayNightChange = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, dayNight: data.currentTarget.checked });
    },
    [setMessageToSend, messageToSend],
  );

  const onDayOnlyChange = useCallback(
    (data) => {
      setMessageToSend({ ...messageToSend, dayOnly: data.currentTarget.checked });
    },
    [setMessageToSend, messageToSend],
  );

  const onRadiusChange = useCallback(
    (dataAsNumber, dataAsString) => {
      setMessageToSend({ ...messageToSend, radius: dataAsNumber });
    },
    [setMessageToSend, messageToSend],
  );

  const getPharmacyDisplayForMultiSelect = useCallback(
    (elem) => {
      return {
        value: elem.pharmacyId,
        label:
          '(' +
          elem.level +
          '- group ' +
          (elem.groupId === undefined ? '' : groupList.find((g) => g.groupId === elem.groupId)!.name) +
          ') - ' +
          elem.apbNumber +
          ' ' +
          elem.name +
          ' @ ' +
          elem.city,
      };
    },
    [groupList],
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {
        setMessageToSend(defaultMessage);
        onClose();
      }}
      icon={IconNames.EDIT}
      title={<TranslatedMessage id={TLCode.MESSAGE_NewMessage} />}
      style={{ width: '900px' }}
    >
      <div className={Classes.DIALOG_BODY}>
        <Form
          initialValues={messageToSend}
          onSubmit={(values) => {
            save(values);
            setMessageToSend(defaultMessage);
          }}
          render={({ handleSubmit, hasValidationErrors, dirty }) => {
            return (
              <form onSubmit={handleSubmit}>
                {!isTest && (
                  <>
                    <RadioGroup
                      selectedValue={messageToSend.selectionMode}
                      onChange={(data) => setSelectionMode(data.currentTarget.value)}
                    >
                      <Radio
                        key={'affialiates-1'}
                        label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_1)}
                        value="affiliates-1"
                        disabled={messageToSend.recipientId !== undefined}
                      />
                      <Radio
                        key={'affiliates-3'}
                        label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_3)}
                        value="affiliates-3"
                        disabled={messageToSend.recipientId !== undefined}
                      />
                      <Radio
                        key={'affiliates'}
                        label={translate(TLCode.MESSAGE_RECIPIENTS_Affiliates_All)}
                        value="affiliates"
                        disabled={messageToSend.recipientId !== undefined}
                      />
                      <Radio
                        key={'group'}
                        label={translate(TLCode.MESSAGE_RECIPIENTS_Group)}
                        value="affiliates-group"
                      />
                      <GroupFormField
                        id="groupSelect"
                        label={TLCode.PHARMACIES_Group}
                        name="groupId"
                        groupList={groupList.filter(
                          (g) => secretariatId === undefined || g.secretariatId === secretariatId,
                        )}
                        isClearable={true}
                        disabled={messageToSend.selectionMode !== 'affiliates-group'}
                      />
                      <Radio key={'one'} label={translate(TLCode.MESSAGE_RECIPIENTS_One)} value="one" />
                      <PharmacyFormField2
                        id="pharmacySelect"
                        name="recipientId"
                        getLabel={(ph) => ph.apbNumber + ' - ' + ph.name}
                        pharmacyList={pharmacyList.sort((a, b) => a.apbNumber - b.apbNumber)}
                        required={messageToSend.selectionMode == 'one'}
                        disabled={messageToSend.selectionMode !== 'one'}
                      />
                      <Radio key={'several'} label={translate(TLCode.MESSAGE_RECIPIENTS_Several)} value="several" />
                      <Select
                        options={pharmacyList.map(getPharmacyDisplayForMultiSelect)}
                        required={messageToSend.selectionMode == 'several'}
                        disabled={messageToSend.selectionMode !== 'several'}
                        isMulti
                        value={messageToSend.recipientIds.map((id) => {
                          return getPharmacyDisplayForMultiSelect(pharmacyList.find((p) => p.pharmacyId === id)!);
                        })}
                        onChange={setRecipientIds}
                      />
                      <br />
                      <Radio
                        key={'onDuty'}
                        label={translate(TLCode.MESSAGE_RECIPIENTS_PharmaciesOnDuty)}
                        value="onDuty"
                      />
                      <DivLine>
                        <DivColumn>
                          {messageToSend.selectionMode == 'onDuty' && (
                            <Label>
                              <b>
                                <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DutyDate} />
                              </b>
                              &nbsp;&nbsp;
                            </Label>
                          )}
                          {messageToSend.selectionMode !== 'onDuty' && (
                            <Label style={{ color: '#aaa' }}>
                              <b>
                                <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DutyDate} />
                              </b>
                              &nbsp;&nbsp;
                            </Label>
                          )}
                          <DateInput
                            value={messageToSend.dutyDate}
                            canClearSelection={false}
                            onChange={(value) => {
                              const utcDate = new Date(
                                Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()),
                              );
                              onDutyDateChange(utcDate);
                            }}
                            disabled={messageToSend.selectionMode !== 'onDuty'}
                          />
                        </DivColumn>
                        <DivColumn>
                          <Checkbox
                            name="dayOnly"
                            value={'dayOnly'}
                            checked={messageToSend.dayOnly}
                            onChange={onDayOnlyChange}
                            disabled={messageToSend.selectionMode !== 'onDuty'}
                          >
                            <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DayOnlyDuty} />
                          </Checkbox>
                          <Checkbox
                            name="nightOnly"
                            value={'nightOnly'}
                            checked={messageToSend.nightOnly}
                            onChange={onNightOnlyChange}
                            disabled={messageToSend.selectionMode !== 'onDuty'}
                          >
                            <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_NightOnlyDuty} />
                          </Checkbox>
                          <Checkbox
                            name="dayNight"
                            value={'dayNight'}
                            checked={messageToSend.dayNight}
                            onChange={onDayNightChange}
                            disabled={messageToSend.selectionMode !== 'onDuty'}
                          >
                            <TranslatedMessage id={TLCode.MESSAGE_RECIPIENTS_DayNightDuty} />
                          </Checkbox>
                        </DivColumn>
                      </DivLine>
                      <Radio
                        key={'neighbors'}
                        label={translate(TLCode.MESSAGE_RECIPIENTS_Neighbors)}
                        value="neighbors"
                      />
                      <DivLine>
                        <DivColumn>
                          <PharmacyFormField
                            id="pharmacySelectNeighbors"
                            name="recipientId"
                            pharmacyList={pharmacyList}
                            required={messageToSend.selectionMode == 'neighbors'}
                            disabled={messageToSend.selectionMode !== 'neighbors'}
                          />
                        </DivColumn>
                        <DivColumn>
                          <NumericInput
                            id="radius"
                            name="radius"
                            required={messageToSend.selectionMode == 'neighbors'}
                            disabled={messageToSend.selectionMode !== 'neighbors'}
                            onValueChange={onRadiusChange}
                            value={messageToSend.radius}
                            majorStepSize={1000}
                            stepSize={100}
                          />
                          {translate(TLCode.MESSAGE_RECIPIENTS_Neighbors_radius)}
                        </DivColumn>
                      </DivLine>
                    </RadioGroup>
                  </>
                )}
                {isTest && (
                  <>
                    <TextInputFormField
                      name="testEmailAddress"
                      label={TLCode.MESSAGE_TestEmailAddress}
                      inline={true}
                      required={true}
                      fill={true}
                    />
                  </>
                )}
                <TextInputFormField
                  name="title"
                  label={TLCode.MESSAGE_Title}
                  inline={true}
                  required={true}
                  fill={true}
                />
                <div>
                  <RTEFormField name="body" label={TLCode.MESSAGE_Content} />
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                  <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button text={<TranslatedMessage id={TLCode.GENERAL_Cancel} />} onClick={onClose} />
                    <Button
                      type="submit"
                      intent={Intent.PRIMARY}
                      disabled={hasValidationErrors || !dirty}
                      text={<TranslatedMessage id={TLCode.GENERAL_Send} />}
                    />
                  </div>
                </div>
              </form>
            );
          }}
        />
      </div>
    </Dialog>
  );
};

export interface UseMessageToSendProps {
  message: MessageToSend;
}

export const useMessageToSend = ({ message }: UseMessageToSendProps) => {
  const [messageToSend, setMessageToSend] = useState(message);

  useEffect(() => {
    setMessageToSend(message);
  }, [message]);

  return { messageToSend, setMessageToSend };
};

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 900px;
  margin-right: 20px;
`;
